import { useState, useRef } from 'react';
import { OnboardingStepProps, CandidateEvidence } from './types';

export function StepCandidate({ state, onUpdate, onNext, onBack }: OnboardingStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [name, setName] = useState(state.candidate?.name ?? '');
  const [resumeFile, setResumeFile] = useState<File | undefined>(state.candidate?.resumeFile);
  const [linkedinContent, setLinkedinContent] = useState(state.candidate?.linkedinContent ?? '');
  const [portfolioUrl, setPortfolioUrl] = useState(state.candidate?.portfolioUrl ?? '');
  const [usingSample, setUsingSample] = useState(state.candidate?.usingSample ?? false);

  const hasEvidence = !!resumeFile || linkedinContent.trim().length > 0 || portfolioUrl.trim().length > 0 || usingSample;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setResumeFile(file);
      setUsingSample(false);
    }
  };

  const handleUseSample = () => {
    setUsingSample(true);
    setResumeFile(undefined);
    setLinkedinContent('');
    setPortfolioUrl('');
    setName('Alex Chen');
  };

  const handleContinue = () => {
    const candidate: CandidateEvidence = {
      name: name.trim() || undefined,
      resumeFile,
      linkedinContent: linkedinContent.trim() || undefined,
      portfolioUrl: portfolioUrl.trim() || undefined,
      usingSample,
    };
    onUpdate({ candidate });
    onNext();
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-surface-900 mb-4">
          Bring in a real candidate
        </h1>
        <p className="text-lg text-surface-500 max-w-xl mx-auto">
          Add whatever you have. Messy is okay—we'll structure it.
        </p>
      </div>

      <div className="max-w-xl mx-auto space-y-6">
        {/* Optional name */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-2">
            Candidate name <span className="text-surface-400 font-normal">— optional</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Alex Chen"
            disabled={usingSample}
            className="w-full px-4 py-3 border-2 border-surface-200 rounded-xl focus:border-brand-500 focus:ring-0 focus:outline-none transition-colors text-surface-900 placeholder:text-surface-400 disabled:bg-surface-100 disabled:text-surface-500"
          />
        </div>

        {/* Resume upload */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-2">
            Resume
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={usingSample}
            className={`
              w-full p-6 border-2 border-dashed rounded-xl transition-all duration-200 text-left
              ${resumeFile
                ? 'border-green-300 bg-green-50'
                : 'border-surface-300 hover:border-brand-400 hover:bg-brand-50/50'
              }
              ${usingSample ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {resumeFile ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-surface-900">{resumeFile.name}</p>
                  <p className="text-sm text-surface-500">Click to replace</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-surface-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-surface-500" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-surface-700">Upload PDF or DOC</p>
                  <p className="text-sm text-surface-500">Drop a file or click to browse</p>
                </div>
              </div>
            )}
          </button>
        </div>

        {/* LinkedIn / Email content */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-2">
            LinkedIn profile or email conversation
          </label>
          <textarea
            value={linkedinContent}
            onChange={(e) => {
              setLinkedinContent(e.target.value);
              if (e.target.value) setUsingSample(false);
            }}
            placeholder="Paste a LinkedIn URL, summary, or recruiter conversation..."
            rows={4}
            disabled={usingSample}
            className="w-full px-4 py-3 border-2 border-surface-200 rounded-xl focus:border-brand-500 focus:ring-0 focus:outline-none transition-colors text-surface-900 placeholder:text-surface-400 resize-none disabled:bg-surface-100 disabled:text-surface-500"
          />
        </div>

        {/* Portfolio / GitHub */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-2">
            GitHub or portfolio link
          </label>
          <input
            type="url"
            value={portfolioUrl}
            onChange={(e) => {
              setPortfolioUrl(e.target.value);
              if (e.target.value) setUsingSample(false);
            }}
            placeholder="https://github.com/..."
            disabled={usingSample}
            className="w-full px-4 py-3 border-2 border-surface-200 rounded-xl focus:border-brand-500 focus:ring-0 focus:outline-none transition-colors text-surface-900 placeholder:text-surface-400 disabled:bg-surface-100 disabled:text-surface-500"
          />
        </div>

        {/* Divider */}
        <div className="relative py-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-surface-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-4 text-sm text-surface-400">or</span>
          </div>
        </div>

        {/* Use sample */}
        <button
          onClick={handleUseSample}
          className={`
            w-full p-5 rounded-xl border-2 transition-all duration-200 text-left
            ${usingSample
              ? 'border-brand-500 bg-brand-50'
              : 'border-surface-200 hover:border-brand-300 hover:bg-brand-50/30'
            }
          `}
        >
          <div className="flex items-center gap-4">
            <div className={`
              w-12 h-12 rounded-xl flex items-center justify-center transition-colors
              ${usingSample ? 'bg-brand-100' : 'bg-surface-100'}
            `}>
              <svg className={`w-6 h-6 ${usingSample ? 'text-brand-600' : 'text-surface-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
            </div>
            <div>
              <p className={`font-semibold ${usingSample ? 'text-brand-700' : 'text-surface-900'}`}>
                Use a sample candidate
              </p>
              <p className="text-sm text-surface-500">
                See how the system works with realistic data
              </p>
            </div>
            {usingSample && (
              <div className="ml-auto">
                <svg className="w-6 h-6 text-brand-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </button>
      </div>

      <div className="mt-12 flex justify-center gap-4">
        <button onClick={onBack} className="btn btn-secondary text-base px-8 py-3.5">
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!hasEvidence}
          className={`
            btn text-base px-10 py-3.5 transition-all duration-200
            ${hasEvidence
              ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-500/25'
              : 'bg-surface-200 text-surface-400 cursor-not-allowed'
            }
          `}
        >
          Analyze Candidate
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

